import {
  BUNDLE_DEFINITIONS,
  PERMANENT_PURCHASE_DEFINITIONS,
  TOKEN_PACK_DEFINITIONS,
  getPermanentPurchaseNextCost,
  type BundleDefinition,
  type PermanentPurchaseDefinition,
  type TokenPackDefinition,
} from "@/features/payment/paymentDefinitions";
import {
  createPaymentCreditsPanel,
  type PaymentCreditsSectionId,
} from "@/features/payment/paymentCreditsPanel";
import { listOwnedPermanentPurchases } from "@/features/payment/paymentPermanentEffects";
import {
  evaluatePurchaseGate,
  type PurchaseGateConfig,
  type PurchaseProductKind,
} from "@/features/payment/purchaseGate";
import { resolvePurchaseGateConfig } from "@/features/payment/purchaseGateConfig";
import { isRealMoneyCheckoutAvailable } from "@/features/payment/shopPurchaseService";
import {
  openPurchaseSignInModal,
  type PurchaseSignInModalHandle,
} from "@/features/payment/purchaseSignInModal";
import { formatCurrency, formatInteger, getMessages } from "@/i18n/runtime";
import type {
  BundleEntitlementKey,
  HoverIdlePaymentState,
  PermanentPurchaseId,
  PermanentPurchaseResult,
  PlayerOwner,
  TokenPackId,
} from "@/features/payment/types";

type PaymentStatusKind = "info" | "error" | "";

interface PaymentStatusUpdate {
  readonly message: string;
  readonly kind: PaymentStatusKind;
}

type ShopTabId = "effects" | "tokens" | "bundles" | "credits";

type BundleCheckoutResult =
  | {
      readonly ok: true;
      readonly payment: HoverIdlePaymentState;
    }
  | {
      readonly ok: false;
      readonly reason: "already-owned" | "checkout-failed";
      readonly payment: HoverIdlePaymentState;
    };

type TokenPackCheckoutResult =
  | {
      readonly ok: true;
      readonly payment: HoverIdlePaymentState;
    }
  | {
      readonly ok: false;
      readonly reason: "checkout-failed";
      readonly payment: HoverIdlePaymentState;
    };

export interface PaymentPanelOptions {
  readonly container: HTMLElement;
  readonly loadState: () => HoverIdlePaymentState;
  readonly onPurchaseEffect: (
    id: PermanentPurchaseId,
  ) => PermanentPurchaseResult;
  readonly onCheckoutBundle: (
    id: BundleEntitlementKey,
  ) => Promise<BundleCheckoutResult>;
  readonly onCheckoutTokenPack: (
    id: TokenPackId,
  ) => Promise<TokenPackCheckoutResult>;
  readonly getOwnerKind: () => PlayerOwner["ownerKind"];
  readonly onRequestSignIn: () => void;
  readonly gateConfig?: PurchaseGateConfig;
}

export interface PaymentPanel {
  update(state: HoverIdlePaymentState): void;
  dispose(): void;
}

interface PurchaseRowNodes {
  readonly definition: PermanentPurchaseDefinition;
  readonly costCell: HTMLDivElement;
  readonly button: HTMLButtonElement;
  readonly state: HTMLSpanElement;
}

interface BundleRowNodes {
  readonly definition: BundleDefinition;
  readonly button: HTMLButtonElement;
  readonly state: HTMLSpanElement;
}

interface TokenPackRowNodes {
  readonly definition: TokenPackDefinition;
  readonly button: HTMLButtonElement;
}

interface ShopTabDef {
  readonly id: ShopTabId;
  readonly draft: boolean;
}

interface PaymentLegalNotice {
  readonly root: HTMLDivElement;
  dispose(): void;
}

interface CheckoutFlowOptions<
  TResult extends { readonly payment: HoverIdlePaymentState },
> {
  readonly isPending: () => boolean;
  readonly setPending: (pending: boolean) => void;
  readonly loadState: () => HoverIdlePaymentState;
  readonly sync: (state: HoverIdlePaymentState) => void;
  readonly run: () => Promise<TResult>;
  readonly setStatus: (message: string, kind?: PaymentStatusKind) => void;
  readonly getStatusFromResult: (result: TResult) => PaymentStatusUpdate;
  readonly getErrorStatus: () => PaymentStatusUpdate;
}

const SHOP_TABS: readonly ShopTabDef[] = [
  { id: "effects", draft: false },
  { id: "tokens", draft: false },
  { id: "bundles", draft: false },
  { id: "credits", draft: false },
] as const;

export function createPaymentPanel(options: PaymentPanelOptions): PaymentPanel {
  const messages = getMessages();
  const cleanups: Array<() => void> = [];

  const root = document.createElement("div");
  root.className = "hover-idle-payment-panel";

  const title = document.createElement("h2");
  title.textContent = messages.payment.title;

  const tabBar = document.createElement("div");
  tabBar.className = "hover-idle-settings-tabs";
  tabBar.setAttribute("role", "tablist");
  tabBar.setAttribute("aria-label", messages.payment.categoriesAria);

  const tokenSummary = document.createElement("p");
  tokenSummary.className = "hover-idle-payment-token";

  const status = document.createElement("p");
  status.className = "hover-idle-payment-status";
  status.setAttribute("role", "status");
  status.setAttribute("aria-live", "polite");

  const sections = new Map<ShopTabId, HTMLDivElement>();
  const tabButtons = new Map<ShopTabId, HTMLButtonElement>();
  let activeTabId: ShopTabId = "effects";
  let isBundleCheckoutPending = false;
  let isTokenPackCheckoutPending = false;

  const creditsPanel = createPaymentCreditsPanel();
  cleanups.push(() => creditsPanel.dispose());

  const setStatus = (message: string, kind: PaymentStatusKind = ""): void => {
    status.textContent = message;
    status.dataset.kind = kind;
  };

  const gateConfig: PurchaseGateConfig =
    options.gateConfig ?? resolvePurchaseGateConfig();
  // Real-money checkout is the only part that depends on a configured payment
  // provider; Token-priced permanent effects stay playable without one.
  const isCheckoutAvailable = isRealMoneyCheckoutAvailable(
    gateConfig.purchaseMode,
  );
  const rejectUnavailableCheckout = (): boolean => {
    if (isCheckoutAvailable) return false;
    setStatus(messages.payment.statusCheckoutUnavailable, "error");
    return true;
  };
  let activeSignInModal: PurchaseSignInModalHandle | null = null;
  cleanups.push(() => {
    activeSignInModal?.dispose();
    activeSignInModal = null;
  });

  const gateBeforePurchase = async (
    productKind: PurchaseProductKind,
  ): Promise<"proceed" | "abort"> => {
    const decision = evaluatePurchaseGate({
      ownerKind: options.getOwnerKind(),
      product: { kind: productKind },
      config: gateConfig,
    });
    if (decision.kind === "allow") return "proceed";
    if (activeSignInModal !== null) return "abort";
    const handle = openPurchaseSignInModal({
      policy: decision.kind === "require-sign-in" ? "require" : "recommend",
    });
    activeSignInModal = handle;
    try {
      const choice = await handle.result;
      if (choice === "sign-in") {
        options.onRequestSignIn();
        return "abort";
      }
      if (
        choice === "continue-as-guest" &&
        decision.kind === "recommend-sign-in"
      ) {
        return "proceed";
      }
      return "abort";
    } finally {
      handle.dispose();
      if (activeSignInModal === handle) {
        activeSignInModal = null;
      }
    }
  };

  // ----- Effects section -----
  const effectsSection = document.createElement("div");
  effectsSection.className = "hover-idle-settings-section";
  effectsSection.dataset.section = "effects";

  const acquiredDashboard = document.createElement("div");
  acquiredDashboard.className = "hover-idle-payment-acquired";

  const acquiredHeading = document.createElement("h3");
  acquiredHeading.className = "hover-idle-payment-acquired-heading";
  acquiredHeading.textContent = messages.payment.acquiredHeading;

  const acquiredEmpty = document.createElement("p");
  acquiredEmpty.className = "hover-idle-payment-acquired-empty";
  acquiredEmpty.textContent = messages.payment.acquiredEmpty;

  const acquiredList = document.createElement("ul");
  acquiredList.className = "hover-idle-payment-acquired-list";

  acquiredDashboard.append(acquiredHeading, acquiredEmpty, acquiredList);

  const effectsDescription = document.createElement("p");
  effectsDescription.className = "hover-idle-payment-description";
  effectsDescription.textContent = messages.payment.effectsDescription;

  const effectsTable = document.createElement("div");
  effectsTable.className = "hover-idle-payment-table";

  const effectsHeaderRow = document.createElement("div");
  effectsHeaderRow.className =
    "hover-idle-payment-row hover-idle-payment-row-header";
  effectsHeaderRow.append(
    createCell(messages.payment.headers.effectCost, "hover-idle-payment-cell"),
    createCell(messages.payment.headers.effectName, "hover-idle-payment-cell"),
    createCell(messages.payment.headers.effectValue, "hover-idle-payment-cell"),
    createCell("", "hover-idle-payment-cell hover-idle-payment-cell-action"),
  );
  effectsTable.append(effectsHeaderRow);

  const effectRows: PurchaseRowNodes[] = PERMANENT_PURCHASE_DEFINITIONS.map(
    (definition) => {
      const row = document.createElement("div");
      row.className = "hover-idle-payment-row";
      row.dataset.effectId = definition.id;

      const costCell = createCell("", "hover-idle-payment-cell");
      const name = createCell(
        messages.payment.permanentNames[definition.id],
        "hover-idle-payment-cell",
      );

      const effectCell = document.createElement("div");
      effectCell.className = "hover-idle-payment-cell";
      const effect = document.createElement("span");
      effect.textContent = messages.payment.permanentEffects[definition.id];
      const state = document.createElement("span");
      state.className = "hover-idle-payment-item-state";
      effectCell.append(effect, state);

      const actionCell = document.createElement("div");
      actionCell.className =
        "hover-idle-payment-cell hover-idle-payment-cell-action";
      const button = document.createElement("button");
      button.type = "button";
      button.className = "hover-idle-payment-button";
      button.textContent = messages.common.purchase;
      const runEffectPurchase = (): void => {
        const result = options.onPurchaseEffect(definition.id);
        sync(result.payment);
        if (result.ok) {
          setStatus(messages.payment.statusPurchasedEffect, "info");
          return;
        }
        if (
          result.reason === "already-purchased" ||
          result.reason === "max-reached"
        ) {
          setStatus(messages.payment.statusAlreadyPurchasedEffect, "error");
          return;
        }
        setStatus(messages.payment.statusInsufficientToken, "error");
      };
      const handleClick = (): void => {
        void (async () => {
          const gate = await gateBeforePurchase("one-time");
          if (gate === "proceed") runEffectPurchase();
        })();
      };
      button.addEventListener("click", handleClick);
      cleanups.push(() => button.removeEventListener("click", handleClick));
      actionCell.append(button);

      row.append(costCell, name, effectCell, actionCell);
      effectsTable.append(row);
      return { definition, costCell, button, state };
    },
  );
  effectsSection.append(acquiredDashboard, effectsDescription, effectsTable);
  sections.set("effects", effectsSection);

  // ----- Tokens section -----
  const tokensSection = document.createElement("div");
  tokensSection.className = "hover-idle-settings-section";
  tokensSection.dataset.section = "tokens";

  const tokensDescription = document.createElement("p");
  tokensDescription.className = "hover-idle-payment-description";
  tokensDescription.textContent = messages.payment.tokensDescription;

  const tokensUnavailableNotice = createCheckoutUnavailableNotice(
    messages,
    isCheckoutAvailable,
  );

  const tokensLegalNotice = createPaymentLegalNotice(messages, {
    context: "tokens",
    onOpenCreditsSection: openCreditsSection,
  });
  cleanups.push(() => tokensLegalNotice.dispose());

  const tokensTable = document.createElement("div");
  tokensTable.className = "hover-idle-payment-table";

  const tokensHeaderRow = document.createElement("div");
  tokensHeaderRow.className =
    "hover-idle-payment-row hover-idle-payment-row-header";
  tokensHeaderRow.append(
    createCell(messages.payment.headers.tokenAmount, "hover-idle-payment-cell"),
    createCell(messages.payment.headers.tokenPrice, "hover-idle-payment-cell"),
    createCell("", "hover-idle-payment-cell hover-idle-payment-cell-action"),
  );
  tokensTable.append(tokensHeaderRow);

  const tokenRows: TokenPackRowNodes[] = TOKEN_PACK_DEFINITIONS.map(
    (definition) => {
      const row = document.createElement("div");
      row.className = "hover-idle-payment-row";
      row.dataset.tokenPackId = definition.id;

      const amount = createCell(
        messages.payment.tokenPackAmount(formatInteger(definition.tokenGrant)),
        "hover-idle-payment-cell",
      );
      const price = createCell(
        formatCurrency(definition.price, definition.currency),
        "hover-idle-payment-cell",
      );

      const actionCell = document.createElement("div");
      actionCell.className =
        "hover-idle-payment-cell hover-idle-payment-cell-action";
      const button = document.createElement("button");
      button.type = "button";
      button.className = "hover-idle-payment-button";
      button.textContent = messages.common.purchase;

      const handleTokenPackClick = async (): Promise<void> =>
        runCheckoutFlow({
          isPending: () => isTokenPackCheckoutPending,
          setPending: (pending) => {
            isTokenPackCheckoutPending = pending;
          },
          loadState: options.loadState,
          sync,
          run: () => options.onCheckoutTokenPack(definition.id),
          setStatus,
          getStatusFromResult: (result) =>
            result.ok
              ? {
                  message: messages.payment.statusPurchasedTokenPack,
                  kind: "info",
                }
              : {
                  message: messages.payment.statusTokenPackCheckoutFailed,
                  kind: "error",
                },
          getErrorStatus: () => ({
            message: messages.payment.statusTokenPackCheckoutFailed,
            kind: "error",
          }),
        });
      const clickListener = (): void => {
        if (rejectUnavailableCheckout()) return;
        void (async () => {
          const gate = await gateBeforePurchase("one-time");
          if (gate === "proceed") await handleTokenPackClick();
        })();
      };
      button.addEventListener("click", clickListener);
      cleanups.push(() => button.removeEventListener("click", clickListener));
      actionCell.append(button);

      row.append(amount, price, actionCell);
      tokensTable.append(row);
      return { definition, button };
    },
  );
  tokensSection.append(
    tokensDescription,
    tokensUnavailableNotice,
    tokensLegalNotice.root,
    tokensTable,
  );
  sections.set("tokens", tokensSection);

  // ----- Bundles section -----
  const bundlesSection = document.createElement("div");
  bundlesSection.className = "hover-idle-settings-section";
  bundlesSection.dataset.section = "bundles";

  const bundlesDescription = document.createElement("p");
  bundlesDescription.className = "hover-idle-payment-description";
  bundlesDescription.textContent = messages.payment.bundlesDescription;

  const bundlesUnavailableNotice = createCheckoutUnavailableNotice(
    messages,
    isCheckoutAvailable,
  );

  const bundlesLegalNotice = createPaymentLegalNotice(messages, {
    context: "bundles",
    onOpenCreditsSection: openCreditsSection,
  });
  cleanups.push(() => bundlesLegalNotice.dispose());

  const bundlesTable = document.createElement("div");
  bundlesTable.className = "hover-idle-payment-table";

  const bundlesHeaderRow = document.createElement("div");
  bundlesHeaderRow.className =
    "hover-idle-payment-row hover-idle-payment-row-header";
  bundlesHeaderRow.append(
    createCell(messages.payment.headers.bundleName, "hover-idle-payment-cell"),
    createCell(
      messages.payment.headers.bundleContents,
      "hover-idle-payment-cell",
    ),
    createCell(messages.payment.headers.bundlePrice, "hover-idle-payment-cell"),
    createCell("", "hover-idle-payment-cell hover-idle-payment-cell-action"),
  );
  bundlesTable.append(bundlesHeaderRow);

  const bundleRows: BundleRowNodes[] = BUNDLE_DEFINITIONS.map((definition) => {
    const row = document.createElement("div");
    row.className = "hover-idle-payment-row";
    row.dataset.bundleId = definition.id;

    const name = createCell(
      `${messages.payment.bundleNames[definition.id]} (${definition.sku})`,
      "hover-idle-payment-cell",
    );
    const contents = createCell(
      formatBundleContents(definition, messages),
      "hover-idle-payment-cell",
    );

    const priceCell = document.createElement("div");
    priceCell.className = "hover-idle-payment-cell";
    const price = document.createElement("span");
    price.textContent = formatCurrency(definition.price, definition.currency);
    const state = document.createElement("span");
    state.className = "hover-idle-payment-item-state";
    priceCell.append(price, state);

    const actionCell = document.createElement("div");
    actionCell.className =
      "hover-idle-payment-cell hover-idle-payment-cell-action";
    const button = document.createElement("button");
    button.type = "button";
    button.className = "hover-idle-payment-button";
    button.textContent = messages.common.purchase;
    const handleBundleClick = async (): Promise<void> =>
      runCheckoutFlow({
        isPending: () => isBundleCheckoutPending,
        setPending: (pending) => {
          isBundleCheckoutPending = pending;
        },
        loadState: options.loadState,
        sync,
        run: () => options.onCheckoutBundle(definition.id),
        setStatus,
        getStatusFromResult: (result) => {
          if (result.ok) {
            return {
              message: messages.payment.statusPurchasedBundle,
              kind: "info",
            };
          }
          if (result.reason === "already-owned") {
            return {
              message: messages.payment.statusAlreadyPurchasedBundle,
              kind: "error",
            };
          }
          return {
            message: messages.payment.statusBundleCheckoutFailed,
            kind: "error",
          };
        },
        getErrorStatus: () => ({
          message: messages.payment.statusBundleCheckoutFailed,
          kind: "error",
        }),
      });
    const clickListener = (): void => {
      if (rejectUnavailableCheckout()) return;
      void (async () => {
        const gate = await gateBeforePurchase("one-time");
        if (gate === "proceed") await handleBundleClick();
      })();
    };
    button.addEventListener("click", clickListener);
    cleanups.push(() => button.removeEventListener("click", clickListener));
    actionCell.append(button);

    row.append(name, contents, priceCell, actionCell);
    bundlesTable.append(row);
    return { definition, button, state };
  });
  bundlesSection.append(
    bundlesDescription,
    bundlesUnavailableNotice,
    bundlesLegalNotice.root,
    bundlesTable,
  );
  sections.set("bundles", bundlesSection);

  // ----- Credits section -----
  const creditsSection = document.createElement("div");
  creditsSection.className = "hover-idle-settings-section";
  creditsSection.dataset.section = "credits";

  creditsSection.append(creditsPanel.root);
  sections.set("credits", creditsSection);

  function setActiveTab(id: ShopTabId): void {
    if (id === activeTabId) return;
    sections.get(activeTabId)!.hidden = true;
    sections.get(id)!.hidden = false;
    for (const [tabId, button] of tabButtons) {
      const isActive = tabId === id;
      button.dataset.active = isActive ? "true" : "false";
      button.setAttribute("aria-selected", isActive ? "true" : "false");
    }
    activeTabId = id;
  }

  function openCreditsSection(id: PaymentCreditsSectionId): void {
    creditsPanel.openSection(id);
    setActiveTab("credits");
  }

  for (const tab of SHOP_TABS) {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "hover-idle-settings-tab";
    if (tab.draft) {
      button.classList.add("hover-idle-settings-tab-draft");
    }
    button.textContent = messages.payment.tabs[tab.id];
    button.dataset.tab = tab.id;
    button.dataset.active = tab.id === activeTabId ? "true" : "false";
    button.setAttribute("role", "tab");
    button.setAttribute(
      "aria-selected",
      tab.id === activeTabId ? "true" : "false",
    );
    const handleClick = (): void => setActiveTab(tab.id);
    button.addEventListener("click", handleClick);
    cleanups.push(() => button.removeEventListener("click", handleClick));
    tabButtons.set(tab.id, button);
    tabBar.append(button);

    const section = sections.get(tab.id)!;
    if (tab.id !== activeTabId) {
      section.hidden = true;
    }
  }

  const sync = (state: HoverIdlePaymentState): void => {
    tokenSummary.textContent = messages.payment.tokenSummary(
      formatInteger(state.token),
    );

    syncEffectRows(effectRows, state, messages);
    syncBundleRows(
      bundleRows,
      state,
      isBundleCheckoutPending || !isCheckoutAvailable,
      messages,
    );
    syncTokenRows(
      tokenRows,
      isTokenPackCheckoutPending || !isCheckoutAvailable,
    );
    renderAcquiredEffects(
      acquiredEmpty,
      acquiredList,
      collectAcquiredEffects(state, messages),
      messages,
    );
  };

  root.append(title, tabBar, tokenSummary);
  for (const tab of SHOP_TABS) {
    root.append(sections.get(tab.id)!);
  }
  root.append(status);
  options.container.replaceChildren(root);
  sync(options.loadState());

  return {
    update(state: HoverIdlePaymentState): void {
      sync(state);
    },
    dispose(): void {
      for (const cleanup of cleanups) cleanup();
      root.remove();
    },
  };
}

function createCheckoutUnavailableNotice(
  messages: ReturnType<typeof getMessages>,
  isCheckoutAvailable: boolean,
): HTMLParagraphElement {
  const notice = document.createElement("p");
  notice.className = "hover-idle-payment-checkout-unavailable";
  notice.dataset.checkoutAvailability = isCheckoutAvailable
    ? "available"
    : "unavailable";
  notice.textContent = messages.payment.statusCheckoutUnavailable;
  notice.hidden = isCheckoutAvailable;
  return notice;
}

function createPaymentLegalNotice(
  messages: ReturnType<typeof getMessages>,
  options: {
    readonly context: "tokens" | "bundles";
    readonly onOpenCreditsSection: (id: PaymentCreditsSectionId) => void;
  },
): PaymentLegalNotice {
  const cleanups: Array<() => void> = [];

  const root = document.createElement("div");
  root.className = "hover-idle-payment-legal-note";
  root.dataset.legalContext = options.context;

  const title = document.createElement("h3");
  title.className = "hover-idle-payment-legal-title";
  title.textContent = messages.payment.purchaseLegalNotice.title;

  const agreement = document.createElement("p");
  agreement.className = "hover-idle-payment-legal-copy";
  agreement.textContent = messages.payment.purchaseLegalNotice.agreement;

  const details = document.createElement("p");
  details.className = "hover-idle-payment-legal-copy";
  details.textContent = messages.payment.purchaseLegalNotice.details;

  const links = document.createElement("div");
  links.className = "hover-idle-payment-legal-links";

  const linkDefs: ReadonlyArray<{
    readonly id: PaymentCreditsSectionId;
    readonly label: string;
  }> = [
    {
      id: "terms",
      label: messages.payment.purchaseLegalNotice.links.terms,
    },
    {
      id: "privacy",
      label: messages.payment.purchaseLegalNotice.links.privacy,
    },
    {
      id: "commerce",
      label: messages.payment.purchaseLegalNotice.links.commerce,
    },
  ];

  for (const linkDef of linkDefs) {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "hover-idle-payment-legal-link";
    button.dataset.legalLink = linkDef.id;
    button.textContent = linkDef.label;

    const handleClick = (): void => {
      options.onOpenCreditsSection(linkDef.id);
    };
    button.addEventListener("click", handleClick);
    cleanups.push(() => button.removeEventListener("click", handleClick));
    links.append(button);
  }

  const footnote = document.createElement("p");
  footnote.className = "hover-idle-payment-legal-footnote";
  footnote.textContent = messages.payment.purchaseLegalNotice.stripeFootnote;

  root.append(title, agreement, details, links, footnote);

  return {
    root,
    dispose(): void {
      for (const cleanup of cleanups) {
        cleanup();
      }
      root.remove();
    },
  };
}

function createCell(text: string, className: string): HTMLDivElement {
  const cell = document.createElement("div");
  cell.className = className;
  cell.textContent = text;
  return cell;
}

interface AcquiredEffectEntry {
  readonly key: string;
  readonly name: string;
  readonly effect: string;
}

function collectAcquiredEffects(
  state: HoverIdlePaymentState,
  messages: ReturnType<typeof getMessages>,
): readonly AcquiredEffectEntry[] {
  const entries: AcquiredEffectEntry[] = [];
  for (const { definition, count } of listOwnedPermanentPurchases(
    state.purchasedPermanentItems,
  )) {
    const effect = formatPermanentEffectTotal(definition, count, messages);
    entries.push({
      key: `permanent:${definition.id}`,
      name: messages.payment.permanentNames[definition.id],
      effect,
    });
  }
  return entries;
}

function syncEffectRows(
  rows: readonly PurchaseRowNodes[],
  state: HoverIdlePaymentState,
  messages: ReturnType<typeof getMessages>,
): void {
  for (const row of rows) {
    const count = state.purchasedPermanentItems[row.definition.id];
    const max = row.definition.maxPurchases;
    const atMax = count >= max;
    const nextCost = getPermanentPurchaseNextCost(row.definition, count);
    row.costCell.textContent = atMax
      ? messages.payment.effectMaxedCost
      : messages.payment.effectCostValue(formatInteger(nextCost));
    row.button.disabled = atMax || state.token < nextCost;
    row.state.textContent = messages.payment.effectOwnedCount(
      formatInteger(count),
      formatInteger(max),
    );
    row.state.dataset.purchased = atMax ? "true" : "false";
  }
}

function syncBundleRows(
  rows: readonly BundleRowNodes[],
  state: HoverIdlePaymentState,
  isCheckoutPending: boolean,
  messages: ReturnType<typeof getMessages>,
): void {
  for (const row of rows) {
    const isPurchased = state.entitlements.includes(row.definition.id);
    row.button.disabled = isPurchased || isCheckoutPending;
    row.state.textContent = isPurchased
      ? messages.common.purchased
      : messages.common.notPurchased;
    row.state.dataset.purchased = isPurchased ? "true" : "false";
  }
}

function syncTokenRows(
  rows: readonly TokenPackRowNodes[],
  isCheckoutPending: boolean,
): void {
  for (const row of rows) {
    row.button.disabled = isCheckoutPending;
  }
}

function renderAcquiredEffects(
  emptyNode: HTMLParagraphElement,
  listNode: HTMLUListElement,
  acquired: readonly AcquiredEffectEntry[],
  messages: ReturnType<typeof getMessages>,
): void {
  if (acquired.length === 0) {
    emptyNode.hidden = false;
    listNode.hidden = true;
    listNode.replaceChildren();
    return;
  }

  emptyNode.hidden = true;
  listNode.hidden = false;
  listNode.replaceChildren(
    ...acquired.map((entry) => {
      const item = document.createElement("li");
      item.className = "hover-idle-payment-acquired-item";
      item.dataset.key = entry.key;
      item.textContent = messages.payment.acquiredEffectEntry(
        entry.name,
        entry.effect,
      );
      return item;
    }),
  );
}

function formatPermanentEffectTotal(
  definition: PermanentPurchaseDefinition,
  count: number,
  messages: ReturnType<typeof getMessages>,
): string {
  if (definition.effect.kind === "mpCapacity") {
    return messages.payment.effectTotalMpCapacity(
      formatInteger(definition.effect.amount * count),
    );
  }
  if (definition.effect.kind === "hovGainPercent") {
    return messages.payment.effectTotalHovGainPercent(
      formatInteger(definition.effect.percent * count),
    );
  }
  return messages.payment.effectTotalTimGainPercent(
    formatInteger(definition.effect.percent * count),
  );
}

function formatBundleContents(
  definition: BundleDefinition,
  messages: ReturnType<typeof getMessages>,
): string {
  const parts: string[] = [];
  if (definition.tokenGrant > 0) {
    parts.push(
      messages.payment.bundlePartToken(formatInteger(definition.tokenGrant)),
    );
  }
  if (definition.hovGrant > 0) {
    parts.push(
      messages.payment.bundlePartHov(formatInteger(definition.hovGrant)),
    );
  }
  if (definition.timGrant > 0) {
    parts.push(
      messages.payment.bundlePartTim(formatInteger(definition.timGrant)),
    );
  }
  for (const permanentId of definition.permanentEffectGrants) {
    parts.push(messages.payment.permanentEffects[permanentId]);
  }
  return parts.join(messages.payment.bundlePartSeparator);
}

async function runCheckoutFlow<
  TResult extends { readonly payment: HoverIdlePaymentState },
>(options: CheckoutFlowOptions<TResult>): Promise<void> {
  if (options.isPending()) {
    return;
  }

  options.setPending(true);
  options.sync(options.loadState());

  try {
    const result = await options.run();
    options.sync(result.payment);
    const status = options.getStatusFromResult(result);
    options.setStatus(status.message, status.kind);
  } catch {
    options.sync(options.loadState());
    const status = options.getErrorStatus();
    options.setStatus(status.message, status.kind);
  } finally {
    options.setPending(false);
    options.sync(options.loadState());
  }
}
